import {
  exerciseAttribute,
  freeAttribute,
  handedOverAttribute,
  missedAttribute,
  queueAttribute,
  recordAttribute,
  versionAttribute,
} from "../content/lianxi/exercise-fields.js";
import { assemblyFull } from "./fuxi-assembly.js";
import { itemGate } from "./lianxi-gate.js";
import { blockHandoverCount } from "./lianxi-handover.js";
import { loadPlace, wantsPlace } from "./lianxi-place.js";
import { ExercisePress } from "./lianxi-post.js";
import {
  type PinziKeeper,
  type PinziPlace,
  PinziExercise,
} from "./lianxi-pinzi.js";
import { type PinziSounds, pinziSounds } from "./lianxi-pinzi-sounds.js";
import { said } from "./xiezi-parts.js";

/**
 * Starting a 拼字 Exercise on a `/lianxi/pinzi/` page.
 *
 * `partials/lianxi/pinzi.html` draws every character, and nothing is fetched
 * but a signed-in reader's place and each character's outlines once it is
 * answered. `./lianxi-place.ts` asks the handler for the place first and
 * writes it onto the block, with `data-cb-lianxi-record` saying where to
 * post, and the Exercise starts at the character the handler left it at. A
 * block with no place on it marks in the browser and keeps nothing. The block
 * is marked ready once the Exercise is built, which is what the browser case
 * waits for.
 *
 * **The parts are chosen by `./fuxi-assembly.ts`**, which `./main-lianxi.ts`
 * starts for the whole page, as a 复习 session does. It hears the taps and the
 * digits and says when the sum is full, and this marks the character then.
 * Enter goes on from a marked character.
 */

/** The block, carrying every character. */
const exerciseSelector = "[data-cb-lianxi-pinzi]";

/** A list of whole numbers off an attribute, as `3,0,5`. */
function indexes(value: string): readonly number[] {
  return value === ""
    ? []
    : value
        .split(",")
        .map((each) => Number(each))
        .filter((at) => Number.isInteger(at) && at >= 0);
}

/**
 * The kept place and the keeper for a block that names where to post, or
 * nothing for a block that keeps no place.
 */
function keptBuilding(
  block: HTMLElement,
  fetching: typeof fetch,
): { readonly kept: PinziPlace; readonly keeper: PinziKeeper } | undefined {
  const address = said(block, recordAttribute);
  const slug = said(block, exerciseAttribute);

  if (address === "" || slug === "") {
    return undefined;
  }

  return {
    kept: {
      queue: indexes(said(block, queueAttribute)),
      missed: indexes(said(block, missedAttribute)),
    },
    keeper: {
      press: new ExercisePress({ fetch: fetching, address, slug }),
      version: block.dataset[versionAttribute] ?? "",
      handedOver: block.dataset[handedOverAttribute] !== undefined,
      handoverCount: blockHandoverCount(block),
      free: block.dataset[freeAttribute] !== undefined,
    },
  };
}

/** A part of the block, by the name after `data-cb-pinzi-`. */
function partOf(block: HTMLElement, name: string): HTMLElement | null {
  return block.querySelector<HTMLElement>(`[data-cb-pinzi-${name}]`);
}

/** Whether a key belongs to something the reader is typing into. */
function typingInto(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/** Build the Exercise inside one block. */
function openBuilding(
  block: HTMLElement,
  fetching: typeof fetch,
  sounds: PinziSounds | undefined,
): void {
  const items = [
    ...block.querySelectorAll<HTMLElement>("[data-cb-pinzi-character]"),
  ];
  const answer = partOf(block, "answer");
  const progress = partOf(block, "progress");
  const next = block.querySelector<HTMLButtonElement>("[data-cb-pinzi-next]");
  const mark = partOf(block, "mark");
  const finished = partOf(block, "finished");
  const finishedLine = partOf(block, "finished-line");

  if (
    items.length === 0 ||
    answer === null ||
    progress === null ||
    next === null ||
    mark === null ||
    finished === null ||
    finishedLine === null
  ) {
    return;
  }

  const kept = keptBuilding(block, fetching);
  const exercise = new PinziExercise({
    items,
    answer,
    progress,
    meter: partOf(block, "meter"),
    next,
    mark,
    hint: partOf(block, "hint"),
    finished,
    finishedLine,
    outlines: said(block, "cbPinziOutlines"),
    kept: kept?.kept,
    keeper: kept?.keeper,
    gate: itemGate(block),
    sounds,
  });

  answer.addEventListener(assemblyFull, () => {
    exercise.check();
  });
  next.addEventListener("click", () => {
    exercise.next();
  });
  partOf(block, "again")?.addEventListener("click", () => {
    exercise.again();
  });

  // The keys are heard on the whole page, so a block taken off it stops
  // hearing them at the next key, rather than answering for a page gone.
  const onKey = (event: KeyboardEvent): void => {
    if (!block.isConnected) {
      block.ownerDocument.removeEventListener("keydown", onKey);

      return;
    }

    if (
      event.key === "Enter" &&
      exercise.isMarked() &&
      !typingInto(event.target) &&
      !(
        event.target instanceof Element &&
        event.target.closest("button, a[href], summary") !== null &&
        event.target !== next
      )
    ) {
      event.preventDefault();
      exercise.next();
    }
  };

  block.ownerDocument.addEventListener("keydown", onKey);
  exercise.start();
  block.dataset["cbLianxiReady"] = "";
}

/**
 * Start the 拼字 Exercise on every block under `root`.
 * @param root - The page, or the part of it to look in.
 * @param fetching - How to post. The window's own unless a test says otherwise.
 * @param soundsFor - What plays a verdict's note and says the character.
 *   `./lianxi-pinzi-sounds.ts` unless a test says otherwise.
 */
export function startPinzi(
  root: ParentNode,
  fetching: typeof fetch = (input, init): Promise<Response> =>
    fetch(input, init),
  soundsFor: (page: Document) => PinziSounds | undefined = pinziSounds,
): void {
  for (const block of root.querySelectorAll<HTMLElement>(exerciseSelector)) {
    const sounds = soundsFor(block.ownerDocument);

    if (wantsPlace(block)) {
      void loadPlace(block, fetching).then(() => {
        openBuilding(block, fetching, sounds);
      });
    } else {
      openBuilding(block, fetching, sounds);
    }
  }
}
